import { Button, Eyebrow, TitleBar } from './atoms'
import { useLang, t } from '../lib/lang'
import { externalUrl } from '../lib/api'
import type { DateLiveEntry, Lang } from '../lib/types'

const MONTHS_IT = ['GEN','FEB','MAR','APR','MAG','GIU','LUG','AGO','SET','OTT','NOV','DIC']
const MONTHS_EN = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC']

function formatDate(isoDate: string, lang: Lang) {
  const d = new Date(isoDate + 'T00:00:00')
  const day = String(d.getUTCDate()).padStart(2, '0')
  const month = (lang === 'it' ? MONTHS_IT : MONTHS_EN)[d.getUTCMonth()]
  const year = d.getUTCFullYear()
  return { day, month, year }
}

function DateRow({ d, first }: { d: DateLiveEntry; first: boolean }) {
  const { lang } = useLang()
  const { day, month, year } = formatDate(d.event_date, lang)
  return (
    <div className="ax-date-row" style={{
      display: 'grid',
      gridTemplateColumns: '120px 1fr 1fr 140px',
      alignItems: 'center', padding: '28px 0',
      borderTop: first ? '1px solid var(--line-inverse)' : 'none',
      borderBottom: '1px solid var(--line-inverse)',
      gap: 24, transition: 'background 180ms',
    }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(232,228,220,0.04)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}>
      <div className="ax-date-day">
        <div style={{
          fontFamily: 'var(--font-display)', fontWeight: 500,
          fontSize: 44, lineHeight: 0.95, letterSpacing: '0.02em',
          color: 'var(--fg-inverse)',
        }}>{day}</div>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 11,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          color: 'var(--fg-inverse-muted)', marginTop: 4,
        }}>{month} · {year}</div>
      </div>
      <div className="ax-date-venue" style={{
        fontFamily: 'var(--font-display)', fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        fontSize: 22, lineHeight: 1.1, color: 'var(--fg-inverse)',
      }}>{d.venue}</div>
      <div className="ax-date-city" style={{
        fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--fg-inverse-muted)',
      }}>
        {d.city}
        {d.event_time && (
          <>
            <br />
            <span style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              {t(lang, 'time_prefix')} {d.event_time}
            </span>
          </>
        )}
      </div>
      <div className="ax-date-info" style={{ textAlign: 'right' }}>
        {(() => {
          const href = externalUrl(d.tickets_url)
          if (!href) return null
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" style={{
              fontFamily: 'var(--font-body)', fontSize: 12,
              fontWeight: 500, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'var(--fg-inverse)',
              textDecoration: 'none', borderBottom: '1px solid var(--fg-inverse)',
              paddingBottom: 2,
            }}>{t(lang, 'info_link')}</a>
          )
        })()}
      </div>
    </div>
  )
}

export function DateLive({ items }: { items: DateLiveEntry[] }) {
  const { lang } = useLang()
  function scrollToContact(e: React.MouseEvent) {
    e.preventDefault()
    document.getElementById('contatti')?.scrollIntoView({ behavior: 'smooth' })
  }
  return (
    <section id="live" className="ax-pad-section" style={{
      background: 'var(--ink-2)', color: 'var(--fg-inverse)',
      padding: '120px 48px', position: 'relative',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div className="ax-header-row" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          marginBottom: 56, gap: 24, flexWrap: 'wrap',
        }}>
          <div>
            <Eyebrow light>{t(lang, 'section_live_eyebrow')}</Eyebrow>
            <TitleBar size={56} light>
              {t(lang, 'section_live_title_a')}<br />{t(lang, 'section_live_title_b')}
            </TitleBar>
          </div>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.55,
            color: 'var(--fg-inverse-muted)', maxWidth: 340, margin: 0, whiteSpace: 'pre-line',
          }}>{t(lang, 'section_live_intro')}</p>
        </div>

        {items.length === 0 ? (
          <p style={{
            fontFamily: 'var(--font-serif)', fontStyle: 'italic',
            fontSize: 22, color: 'var(--fg-inverse-muted)', margin: 0,
          }}>{t(lang, 'section_live_empty')}</p>
        ) : (
          <div>
            {items.map((d, i) => <DateRow key={d.id} d={d} first={i === 0} />)}
          </div>
        )}

        <div style={{ marginTop: 48, display: 'flex', gap: 12 }}>
          <Button variant="ghostLight" href="#contatti" onClick={scrollToContact}>
            {t(lang, 'section_live_propose')}
          </Button>
        </div>
      </div>
    </section>
  )
}
